import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/actions/userActions';

import SelectBox from '../SelectBox';
import TextInput from '../TextInput';
import ReportStatusBadge from '../ReportStatusBadge';
import ContentStyles from './Content.module.css';

const TIMEZONE_ALIASES = {
	UTC: 'UTC',
	GMT: 'UTC',
	CST: 'America/Chicago',
	CDT: 'America/Chicago',
	EST: 'America/New_York',
	EDT: 'America/New_York',
	MST: 'America/Denver',
	MDT: 'America/Denver',
	PST: 'America/Los_Angeles',
	PDT: 'America/Los_Angeles'
};

const resolveTimezone = (timezone) => {
	const raw = typeof timezone === 'string' ? timezone.trim() : '';
	if (!raw) return 'UTC';

	const candidate = TIMEZONE_ALIASES[raw] || raw;
	try {
		// Validate timezone for Intl APIs.
		new Intl.DateTimeFormat('en-US', { timeZone: candidate }).format(new Date());
		return candidate;
	} catch (_err) {
		return 'UTC';
	}
};

const Header = ({
	todaysDate,
	headerTitle,
	selectBox,
	selectBoxValue,
	selectOnchange,
	options,
	searchBox = false,
	searchIcon = 'search',
	searchPlaceholder = 'Search here',
	searchValue,
	searchOnChange,
	addBtn = false,
	addOnClick
}) => {
	const dispatch = useDispatch();
	// The signed-in username, shown in the top-right next to the account links.
	const username = useSelector((s) => s.userLogin?.userInfo?.session?.username) || '';
	const [userTimezone, setUserTimezone] = useState('UTC');
	const [isLoading, setIsLoading] = useState(true);
	
	// Fetch user timezone from session on component mount
	useEffect(() => {
		const fetchUserSession = async () => {
			try {
				const response = await fetch('/api/account', {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				});
				
					if (response.ok) {
						const data = await response.json();
						if (data.status && data.session && data.session.timezone) {
							setUserTimezone(resolveTimezone(data.session.timezone));
						}
					}
				} catch (error) {
				console.error('Error fetching user timezone:', error);
			} finally {
				setIsLoading(false);
			}
		};
		
		fetchUserSession();
	}, []);
	
	// Format time using the user's timezone
	const formatTimeWithTimezone = (dateString) => {
		if (!dateString) return '';
		
		try {
			const date = new Date(dateString);
			const normalizedTimezone = resolveTimezone(userTimezone);
			
			// Format the time using the user's timezone
			if (normalizedTimezone !== 'UTC') {
				const timeOptions = { 
					timeZone: normalizedTimezone,
					hour: '2-digit', 
					minute: '2-digit', 
					second: '2-digit', 
					hour12: false 
				};
				
				const dateOptions = {
					timeZone: normalizedTimezone,
					year: 'numeric',
					month: '2-digit',
					day: '2-digit'
				};
				
				// Get timezone abbreviation
				const tzOptions = { 
					timeZone: normalizedTimezone, 
					timeZoneName: 'short' 
				};
				
				const timeFormatter = new Intl.DateTimeFormat('en-US', timeOptions);
				const dateFormatter = new Intl.DateTimeFormat('en-US', dateOptions);
				const tzFormatter = new Intl.DateTimeFormat('en-US', tzOptions);
				
				// Extract timezone abbreviation
				const tzString = tzFormatter.format(date);
				const tzParts = tzString.split(' ');
				const timezoneSuffix = tzParts[tzParts.length - 1];
				
				return `${timeFormatter.format(date)} ${timezoneSuffix} | ${dateFormatter.format(date)}`;
			} else {
				// Fallback to UTC display
				return `${date.getUTCHours()}:${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')} UTC | ${dateString.split('T')[0]}`;
			}
		} catch (error) {
			// Fallback to original format if error
			const date = new Date(dateString);
			return `${date.getUTCHours()}:${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')} UTC | ${dateString.split('T')[0]}`;
		}
	};
	
	return (
		<div className={ContentStyles.headerContainer}>
			<div className={ContentStyles.header_heading}>
				<h1 className={ContentStyles.header_heading_text} data-testid='header-title'>
					{headerTitle}
				</h1>

				{addBtn && (
					<button className={ContentStyles.table_header_btn} onClick={addOnClick} data-testid='add-button'>
						<svg className={ContentStyles.table_header_btn_icon}>
							<use xlinkHref={`/assets/sprite.svg#icon-plus`} />
						</svg>
					</button>
				)}

				{searchBox && (
					<TextInput
						icon={searchIcon}
						placeholder={searchPlaceholder}
						value={searchValue}
						onChange={searchOnChange}
						style={{
							padding: '.5rem 1rem',
							width: '20rem'
						}}
					/>
				)}

				{selectBox && (
					<SelectBox style={{ width: '20rem' }} value={selectBoxValue} onChange={selectOnchange}>
						{options}
					</SelectBox>
				)}
			</div>
			{todaysDate !== '' && (
					<div style={{ marginTop: '1rem' }} data-testid='show-todays-date'>
						<p className={ContentStyles.todaysDate}>
							{isLoading ? 'Loading...' : formatTimeWithTimezone(todaysDate)}
						</p>
					</div>
				)}
			<div className={ContentStyles.header_links}>
				<ReportStatusBadge />
				{username && (
					<span className={ContentStyles.header_user} title='Signed in'>
						<svg className={ContentStyles.header_user_icon} aria-hidden='true'>
							<use xlinkHref='/assets/sprite.svg#icon-user' />
						</svg>
						{username}
					</span>
				)}
				<Link to='/my-account' className={ContentStyles.header_link}>
					My Account
				</Link>
				<button to='/login' onClick={() => dispatch(logout())} className={ContentStyles.header_link_logout}>
					Logout
				</button>
			</div>
		</div>
	);
};

export default Header;
